import React, { useState, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import { IntercomConversation, IntercomMessage } from '../../types/intercom';
import { useTypedNotification } from '../../hooks/useTypedNotification';
import { useSocket } from '../../contexts/SocketContext';

interface ConversationDetailsProps {
  conversation: IntercomConversation;
}

const ConversationDetails: React.FC<ConversationDetailsProps> = ({ conversation }) => {
  const { showNotification } = useTypedNotification();
  const { socket } = useSocket();
  const [messages, setMessages] = useState<IntercomMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, [conversation.id]);

  // Socket event listeners for real-time updates
  useEffect(() => {
    if (!socket) return;

    // Join the company-specific Intercom room
    const token = localStorage.getItem('token');
    if (token) {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.company_id) {
        socket.emit('join', `company_${user.company_id}_intercom`);
        console.log(`Joined Intercom room: company_${user.company_id}_intercom`);
      }
    }

    const handleIntercomEvent = (eventData: any) => {
      console.log('Received Intercom event:', eventData);
      
      switch (eventData.type) {
        case 'message_received':
          // Check if this message belongs to the current conversation
          if (eventData.data.conversationId === conversation.id) {
            setMessages(prevMessages => {
              // Check if message already exists to avoid duplicates
              const messageExists = prevMessages.some(msg => msg.id === eventData.data.message.id);
              if (messageExists) {
                return prevMessages;
              }
              
              // Add new message and sort by creation time
              const updatedMessages = [...prevMessages, eventData.data.message];
              return updatedMessages.sort((a, b) => {
                const timeA = new Date(a.created_at_intercom || a.created_at || 0).getTime();
                const timeB = new Date(b.created_at_intercom || b.created_at || 0).getTime();
                return timeA - timeB;
              });
            });
          }
          break;

        case 'reply_sent':
          // Handle immediate reply feedback (from CRM-sent replies)
          if (eventData.data.conversationId === conversation.id) {
            console.log('Reply sent confirmation received');
            // The reply will also trigger a message_received event when stored, so no need to duplicate
          }
          break;
        
        case 'conversation_updated':
          // Could handle conversation state changes here if needed
          console.log('Conversation updated:', eventData.data.conversation);
          break;
        
        default:
          console.log('Unhandled Intercom event type:', eventData.type);
      }
    };

    socket.on('intercom_event', handleIntercomEvent);

    // Cleanup
    return () => {
      socket.off('intercom_event', handleIntercomEvent);
    };
  }, [socket, conversation.id]);

  // Filter out empty messages for display
  const visibleMessages = messages.filter(message => 
    message.body || message.subject || (message.attachments && message.attachments.length > 0)
  );

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/intercom/conversations/${conversation.id}/messages`, {
        headers: { 'x-auth-token': token }
      });
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      showNotification('Failed to load conversation messages', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getMessageTypeIcon = (messageType: string, authorType: string) => {
    if (authorType === 'admin') {
      return (
        <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
          <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
          </svg>
        </div>
      );
    } else {
      return (
        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case 'open':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      case 'snoozed':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim()) {
      showNotification('Please enter a message', 'error');
      return;
    }

    try {
      setSendingReply(true);
      const token = localStorage.getItem('token');
      
      await axios.post(`/api/intercom/conversations/${conversation.id}/reply`, {
        message: replyMessage.trim()
      }, {
        headers: { 'x-auth-token': token }
      });

      setReplyMessage('');
      showNotification('Reply sent successfully', 'success');
      
      // Note: No need to manually refresh messages - the real-time socket updates will handle this
    } catch (error) {
      console.error('Error sending reply:', error);
      const axiosError = error as AxiosError<{ error?: string }>;
      showNotification(axiosError.response?.data?.error || 'Failed to send reply', 'error');
    } finally {
      setSendingReply(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendReply();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Conversation Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <h2 className="text-xl font-semibold text-gray-900">
                {conversation.lead?.name || conversation.contact?.name || 'Website Visitor'}
              </h2>
              <span className={`px-3 py-1 text-sm rounded-full ${getStateColor(conversation.state || 'unknown')}`}>
                {conversation.state || 'unknown'}
              </span>
              {conversation.priority === 'priority' && (
                <span className="px-3 py-1 text-sm bg-red-100 text-red-800 rounded-full flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Priority
                </span>
              )}
            </div>
            
            {(conversation.lead?.email || conversation.contact?.email) && (
              <p className="text-gray-600 mb-1">
                {conversation.lead?.email || conversation.contact?.email}
              </p>
            )}
            
            {conversation.lead && (
              <div className="flex items-center text-sm text-gray-500 mb-2">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Linked to Lead #{conversation.lead.id}
              </div>
            )}

            {conversation.source_url && (
              <div className="flex items-center text-sm text-gray-500 mb-2">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                <span className="mr-2">Started from:</span>
                <a 
                  href={conversation.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 underline truncate max-w-xs"
                  title={conversation.source_url}
                >
                  {conversation.source_url}
                </a>
              </div>
            )}

            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>{conversation.conversation_message_count || 0} messages</span>
              {conversation.assignee_name && (
                <span>Assigned to {conversation.assignee_name}</span>
              )}
              {conversation.updated_at_intercom && (
                <span>Last updated {formatDateTime(conversation.updated_at_intercom)}</span>
              )}
            </div>
          </div>

          <div className="flex space-x-2">
            {conversation.lead && (
              <a
                href={`/leads?id=${conversation.lead.id}`}
                className="inline-flex items-center px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                View Lead
              </a>
            )}
          </div>
        </div>

        {conversation.tags && conversation.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {conversation.tags.map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">Loading messages...</p>
          </div>
        ) : visibleMessages.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-1">No messages found</h3>
            <p className="text-sm text-gray-600">
              This conversation doesn't have any messages yet.
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto p-4">
            {visibleMessages.map((message) => {
              const isCustomer = message.author_type === 'lead' || message.author_type === 'user';
              const isBot = message.author_type === 'bot';
              const isAgent = message.author_type === 'admin';
              
              return (
                <div key={message.id} className={`flex ${isCustomer ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    isCustomer 
                      ? 'bg-blue-500 text-white' 
                      : isBot 
                        ? 'bg-gray-100 text-gray-800 border border-gray-200'
                        : 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                  }`}>
                    {/* Author and timestamp */}
                    <div className={`text-xs mb-1 ${isCustomer ? 'text-blue-100' : 'text-gray-500'}`}>
                      <span className="font-medium">
                        {message.author_name || (message.author_type === 'lead' ? 'Website Visitor' : 
                         message.author_type === 'admin' ? 'Support Agent' : 
                         message.author_type === 'bot' ? 'Bot' : 'Unknown')}
                      </span>
                      <span className="ml-2">
                        {message.created_at_intercom ? formatDateTime(message.created_at_intercom) : 
                         message.created_at ? formatDateTime(message.created_at) : 'Unknown time'}
                      </span>
                    </div>
                    
                    {/* Subject if present */}
                    {message.subject && (
                      <div className={`text-sm font-medium mb-1 ${isCustomer ? 'text-blue-100' : 'text-gray-700'}`}>
                        {message.subject}
                      </div>
                    )}
                    
                    {/* Message content */}
                    <div 
                      className={`text-sm ${isCustomer ? 'text-white' : 'text-gray-700'}`}
                      dangerouslySetInnerHTML={{ __html: message.body || message.subject || 'System message' }}
                    />

                    {/* Attachments */}
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="mt-2">
                        <div className={`text-xs ${isCustomer ? 'text-blue-100' : 'text-gray-500'}`}>
                          📎 {message.attachments.length} attachment(s)
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reply Section - Only show if conversation is open */}
      {conversation.state === 'open' && (
        <div className="p-6 border-t border-gray-200">
          <div className="flex items-start space-x-3">
            <div className="flex-1">
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your reply..."
                className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                rows={3}
                disabled={sendingReply}
              />
            </div>
            <button
              onClick={sendReply}
              disabled={sendingReply || !replyMessage.trim()}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sendingReply ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Send Reply
                </>
              )}
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      )}

      {/* Closed conversation notice */}
      {conversation.state === 'closed' && (
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center text-gray-600">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            This conversation is closed and cannot receive new replies.
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationDetails;